import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/atoms';
import { ChartCard } from '@/components/Charts/ChartCard';
import { useChartTheme } from '@/components/Charts/theme';
import { useBudgetHistory, useBudgets, useUpsertBudget } from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { Budget } from '@/types/config';

import styles from './ConfigCustos.module.css';

// posicao de cada provedor na paleta de series (a cor sai do tema corrente)
const PROVIDER_SLOT: Record<string, number> = {
  openai: 0,
  anthropic: 1,
  google_ai: 2,
  perplexity: 3,
  serp: 4,
  dalle: 5,
};

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

interface ChartPoint {
  month: string;
  [provider: string]: number | string;
}

export function ConfigCustos() {
  const { chartTheme, chartTooltipStyle } = useChartTheme();
  const { data: budgets = [] } = useBudgets();
  const { data: history = [] } = useBudgetHistory(6);
  const upsertBudget = useUpsertBudget();

  const [editing, setEditing] = useState<Budget | null>(null);
  const [form, setForm] = useState({
    budget_usd: '',
    alert_thresholds: '80,100',
  });
  const [msg, setMsg] = useState<string | null>(null);

  const total = useMemo(() => {
    return budgets.reduce(
      (acc, b) => {
        acc.spent += Number(b.spent_usd);
        acc.budget += Number(b.budget_usd);
        return acc;
      },
      { spent: 0, budget: 0 },
    );
  }, [budgets]);

  const chartData = useMemo<ChartPoint[]>(() => {
    const byMonth = new Map<string, ChartPoint>();
    for (const h of history) {
      const month = h.period_month.slice(0, 7);
      const cur = byMonth.get(month) ?? { month };
      cur[h.provider] = Number(h.spent_usd);
      byMonth.set(month, cur);
    }
    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  }, [history]);

  const providersInChart = useMemo(() => {
    const set = new Set<string>();
    for (const point of chartData) {
      for (const key of Object.keys(point)) {
        if (key !== 'month') set.add(key);
      }
    }
    return [...set];
  }, [chartData]);

  function openEdit(b: Budget) {
    setEditing(b);
    setForm({
      budget_usd: String(b.budget_usd),
      alert_thresholds: b.alert_thresholds.join(','),
    });
    setMsg(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);
    try {
      const thresholds = form.alert_thresholds
        .split(',')
        .map((s) => Number.parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
      await upsertBudget.mutateAsync({
        provider: editing.provider,
        payload: {
          budget_usd: form.budget_usd,
          alert_thresholds: thresholds,
        },
      });
      setMsg(`Budget de ${editing.provider} atualizado.`);
      setEditing(null);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>Custos & Limites</h1>
        <p className={styles.subtitle}>
          Gasto mensal por provider + budgets + histórico de 6 meses. Worker de custos vai atualizar{' '}
          <code>spent_usd</code> em tempo real.
        </p>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Total gasto (mês)</span>
          <span className={styles.kpiValue}>${total.spent.toFixed(2)}</span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Total budget</span>
          <span className={styles.kpiValue}>${total.budget.toFixed(2)}</span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Margem</span>
          <span className={styles.kpiValue}>
            ${Math.max(total.budget - total.spent, 0).toFixed(2)}
          </span>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Gasto</th>
            <th>Budget</th>
            <th>%</th>
            <th>Alertas</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {budgets.map((b) => {
            const pct = b.spent_pct;
            return (
              <tr key={b.id}>
                <td>{b.provider}</td>
                <td className={styles.mono}>${Number(b.spent_usd).toFixed(4)}</td>
                <td className={styles.mono}>${Number(b.budget_usd).toFixed(2)}</td>
                <td>
                  <div className={styles.bar}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${Math.min(pct, 100)}%`,
                        background:
                          pct >= 100 ? 'var(--crit)' : pct >= 80 ? 'var(--warn)' : 'var(--ok)',
                      }}
                    />
                  </div>
                  <span className={styles.mutedSmall}>{pct.toFixed(1)}%</span>
                </td>
                <td className={styles.mono}>{b.alert_thresholds.join('%, ')}%</td>
                <td>
                  <Button size="sm" variant="secondary" onClick={() => openEdit(b)}>
                    Editar
                  </Button>
                </td>
              </tr>
            );
          })}
          {budgets.length === 0 && (
            <tr>
              <td colSpan={6} className={styles.muted}>
                Sem budgets para este mês. Configure nos cards de provider.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <ChartCard title="Custos por mês — 6 meses" subtitle="USD spent_usd">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke={chartTheme.axis} style={{ fontSize: 12 }} />
            <YAxis
              stroke={chartTheme.axis}
              style={{ fontSize: 12 }}
              tickFormatter={(v) => `$${v}`}
            />
            <Tooltip contentStyle={chartTooltipStyle} />
            <Legend />
            {providersInChart.map((p, i) => (
              <Line
                key={p}
                dataKey={p}
                stroke={chartTheme.series[(PROVIDER_SLOT[p] ?? i) % chartTheme.series.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {editing && (
        <div className={styles.modalBackdrop} onClick={() => setEditing(null)}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h2 className={styles.modalTitle}>Budget {editing.provider}</h2>

            <label className={styles.field}>
              <span>Budget mensal (USD)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.budget_usd}
                onChange={(e) => setForm({ ...form, budget_usd: e.target.value })}
              />
            </label>

            <label className={styles.field}>
              <span>Alertas (% — separar por vírgula)</span>
              <input
                value={form.alert_thresholds}
                onChange={(e) => setForm({ ...form, alert_thresholds: e.target.value })}
              />
            </label>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button type="submit" loading={upsertBudget.isPending}>
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
